<script setup>
import { ref, computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { getVariablesForTrigger } from 'flow_builder/constants/flowVariables.js';

const { t } = useI18n();
const resolveLabel = label => label?.startsWith('FLOW_BUILDER.') ? t(label) : (label ?? '');

const props = defineProps({
  actionName: { type: String, default: '' },
  modelValue: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['update:modelValue']);

const flowTriggerEvent = inject('flowTriggerEvent', null);
const flowDynamicVariables = inject('flowDynamicVariables', null);

const availableVariables = computed(() => {
  const base = getVariablesForTrigger(flowTriggerEvent?.value ?? null);
  const dynamic = flowDynamicVariables?.value ?? [];
  return [...base, ...dynamic];
});

function update(patch) {
  emit('update:modelValue', { ...props.modelValue, ...patch });
}

const activeField = ref(null);
const varSearch = ref('');

const filteredVars = computed(() => {
  const q = varSearch.value.toLowerCase();
  if (!q) return availableVariables.value;
  return availableVariables.value.filter(v => resolveLabel(v.label).toLowerCase().includes(q) || v.key.toLowerCase().includes(q));
});

function openPicker(field) {
  activeField.value = field;
  varSearch.value = '';
}

function closePicker() {
  activeField.value = null;
  varSearch.value = '';
}

function selectVar(varKey, fieldKey) {
  update({ [fieldKey]: `{{${varKey}}}` });
  closePicker();
}

const FIELDS = computed(() => [
  {
    key: 'name',
    label: t('FLOW_BUILDER.ACTION_FORMS.CREATE_CONTACT.NAME'),
    placeholder: t('FLOW_BUILDER.ACTION_FORMS.CREATE_CONTACT.NAME_PLACEHOLDER'),
  },
  {
    key: 'email',
    label: t('FLOW_BUILDER.ACTION_FORMS.CREATE_CONTACT.EMAIL'),
    placeholder: t('FLOW_BUILDER.ACTION_FORMS.CREATE_CONTACT.EMAIL_PLACEHOLDER'),
  },
  {
    key: 'phone',
    label: t('FLOW_BUILDER.ACTION_FORMS.CREATE_CONTACT.PHONE'),
    placeholder: t('FLOW_BUILDER.ACTION_FORMS.CREATE_CONTACT.PHONE_PLACEHOLDER'),
  },
]);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div v-for="field in FIELDS" :key="field.key" class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ field.label }}</span>
      <div v-on-click-outside="closePicker" class="relative">
        <div class="flex items-center gap-1 px-2 w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand transition-all">
          <input
            class="reset-base flex-1 min-w-0 h-10 bg-transparent !text-sm !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
            :value="modelValue?.[field.key] ?? ''"
            :placeholder="field.placeholder"
            @input="update({ [field.key]: $event.target.value })"
          />
          <button
            type="button"
            class="shrink-0 inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
            :title="t('FLOW_BUILDER.COMMON.INSERT_VARIABLE')"
            @click.stop="openPicker(field.key)"
          >
            <span class="i-ph-brackets-curly size-3.5 flex-shrink-0" />
          </button>
        </div>
        <div
          v-if="activeField === field.key"
          class="absolute top-full right-0 mt-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
        >
          <div class="px-2 pt-2 pb-1">
            <div class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand">
              <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
              <input
                v-model="varSearch"
                type="text"
                class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                :placeholder="t('FLOW_BUILDER.COMMON.SEARCH_VARIABLE')"
                autofocus
              />
            </div>
          </div>
          <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
            <button
              v-for="v in filteredVars"
              :key="v.key"
              type="button"
              class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="selectVar(v.key, field.key)"
            >
              {{ resolveLabel(v.label) }}
            </button>
            <p v-if="!filteredVars.length" class="px-3 py-2 text-xs text-n-slate-9 italic">{{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
